import AuthGuard from "@/components/AuthGuard";
import ErrorPage from "@/components/pages/error_pages/ErrorPage";
import {lazy} from "react";
import {Navigate, Outlet, RouteObject} from "react-router-dom";
import SettingsPage from "@/components/pages/settings/SettingsPage";
import ClientsPage from "@clients/ClientsPage";
import TaskDetailPage from "@/components/pages/tasks/task_detail/TaskDetailPage";
import Unauthorized from "@/components/pages/error_pages/Unauthorized";
import ConsentPage from "@/components/pages/ConsentPage";
import FailedAttestation from "@/components/pages/error_pages/FailedAttestation";
import FailedPhoneVerification from "@/components/pages/error_pages/FailedPhoneVerification";
import NotificationsPage from "@/components/pages/notifications/NotificationsPage";
import MessagesPage from "@/components/pages/messages/MessagesPage";
import Logout from "@/components/Logout";
import PasswordExpired from "@/components/pages/error_pages/PasswordExpired";
import ToolsPage from "@/components/pages/tools/ToolsPage";
import AnalyticsPage from "@/components/pages/analytics/AnalyticsPage";
import SSOAccessDenied from "@/components/pages/error_pages/SSOAccessDenied";
import NetworkRegistryPage from "@/components/pages/network_registry/NetworkRegistryPage";
import ReferralsPage from "@/components/pages/referrals/ReferralsPage";
import {FeatureToggle} from "@/api/dto/dto";
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions";
import RedirectWithParams from "@/components/RedirectWithParams";
import BhAppointmentDetailPage from "@/components/pages/tasks/task_detail/BhAppointmentDetailPage";
import TaskPage from "@/components/pages/tasks/TaskPage";

const routes: RouteObject[] = [
  {
    path: '/',
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/login',
        element: <ConsentPage login />
      },
      {
        path: '/logout',
        element: <Logout />
      },
      {
        path: '/attestation',
        element: <ConsentPage />
      },
      {
        path: '/complete_attestation',
        element: <FailedAttestation />
      },
      {
        path: '/failed_phone_verification',
        element: <FailedPhoneVerification />
      },
      {
        path: '/unauthorized',
        element: <Unauthorized />
      },
      {
        path: '/password_expired',
        element: <PasswordExpired />
      },
      {
        path: '/sso_access_denied',
        element: <SSOAccessDenied/>
      },
      {
        index: true,
        element: <Navigate to="/clients" />
      },
      {
        path: '/clients',
        handle: { title: 'Clients' },
        children: [
          {
            index: true,
            element: <AuthGuard Component={ClientsPage} />
          },
          {
            path: 'profile/:tab?',
            handle: { title: 'Client Profile' },
            element: <AuthGuard
              featurePermissionsCheck={
                {
                  // @ts-expect-error client profile is not available in new permissions
                  features: [{
                    level: FeaturePermissionLevel.VIEW,
                    feature: Feature.CLIENT_PROFILE
                  }]
                }
              }
              hasCurrentRosterAccess={true}
              Component={lazy(() => import('@clients/profile_page/ClientProfilePage'))}
            />
          }
        ]
      },
      {
        path: '/tasks',
        handle: { title: 'Tasks' },
        element: <AuthGuard featurePermissionsCheck={
          {
            features: [{
              level: FeaturePermissionLevel.VIEW,
              featureNew: FeatureNew.TASKS,
              feature: Feature.TASKS
            }]
          }
        } Component={Outlet} />,
        children: [
          { index: true, element: <RedirectWithParams to="/tasks/care_journeys" /> },
          {path: 'care_journeys', element: <TaskPage/>},
          {path: 'provider_referrals', element: <TaskPage/>},
          {
            path: 'details',
            handle: {title: 'Task Details'},
            children: [
              {
                index: true,
                element: <TaskDetailPage />
              },
              {
                path: 'appointment',
                handle: { title: 'Appointment Detail' },
                element: <BhAppointmentDetailPage />
              }
            ]
          }
        ]
      },
      {
        path: '/profile/:tab?',
        handle: { title: 'My Profile' },
        element: <AuthGuard Component={lazy(() => import('@/components/pages/profile/ProfilePage'))} />
      },
      {
        path: '/admin/:tab?',
        handle: { title: 'Admin' },
        element: <AuthGuard featurePermissionsCheck={
          {
            features: [{
              level: FeaturePermissionLevel.VIEW,
              featureNew: FeatureNew.ADMIN_MANAGEMENT,
              feature: Feature.USER_PERMISSIONS
            }]
          }
        } Component={lazy(() => import('@admin/AdminPage'))} />
      },
      {
        path: '/settings/:tab?',
        handle: { title: 'Settings' },
        element: <AuthGuard bambooAdminOnly={true} Component={SettingsPage} />
      },
      {
        path: '/tools/:tab?',
        handle: { title: 'Tools' },
        element: <AuthGuard bambooAdminOnly={true} Component={ToolsPage} />
      },
      // Redirect old /client_profile URLs to /clients/profile
      {
        path: '/client_profile/:tab?',
        element: <RedirectWithParams to="/clients/profile" />
      },
      {
        path: '/notifications',
        handle: { title: 'Notifications' },
        element: <AuthGuard
          featurePermissionsCheck={
            {
              // @ts-expect-error Notifications is not available in new permissions
              features: [{
                level: FeaturePermissionLevel.VIEW,
                feature: Feature.NOTIFICATIONS
              }]
            }
          }
          hasCurrentRosterAccess={false} Component={NotificationsPage} />
      },
      {
        path: '/messages',
        handle: { title: 'Messages' },
        element: <AuthGuard Component={MessagesPage} />
      },
      {
        path: '/analytics/:tab?',
        handle: { title: 'Analytics' },
        element: <AuthGuard  Component={AnalyticsPage} featureToggleCheck={FeatureToggle.ANALYTICS_TAB}/>
      },
      {
        path: '/network_registry',
        handle: { title: 'Network Registry' },
        element: <AuthGuard featurePermissionsCheck={
          {
            features: [{
              level: FeaturePermissionLevel.VIEW,
              featureNew: FeatureNew.MY_NETWORK,
              feature: Feature.NETWORK_REGISTRY
            },
            {
              level: FeaturePermissionLevel.VIEW,
              featureNew: FeatureNew.NETWORK_REGISTRY_BROWSER,
              feature: Feature.NETWORK_REGISTRY
            },
            {
              level: FeaturePermissionLevel.VIEW,
              featureNew: FeatureNew.NETWORK_FACILITIES_UNITS,
              feature: Feature.NETWORK_REGISTRY
            }],
            strategy: 'OR'
          }
        } Component={NetworkRegistryPage} currentRosterCheck={true}/>
      },
      {
        path: '/referrals',
        handle: { title: 'Referrals' },
        element: <AuthGuard featurePermissionsCheck={
          {
            features: [{
              level: FeaturePermissionLevel.VIEW,
              featureNew: FeatureNew.REFERRALS,
              feature: Feature.REFERRALS
            }]
          }
        } Component={ReferralsPage} currentRosterCheck={true}/>
      }
    ],
  },
  {
    path: '*',
    element: <Navigate to="/clients" />
  },
];

export default routes;
